/**
 * Cover de la page d'achat d'une SKILLCARD : ce que l'élève obtient au bout du
 * chemin. La créature origami sous sa forme ÉVOLUÉE, entourée de l'aura
 * à pleine puissance (HORA MAX, 100 %), dans la teinte de la carte,
 * avec le titre, le(s) niveau(x) et l'objectif HORA MAX.
 *
 * Purement présentationnelle : les données viennent de `get_public_card`
 * (dernier instantané publié). `prefers-reduced-motion` fige l'aura (CSS).
 */

import type { CSSProperties } from 'react';
import { cardPalette, formatHora, type CardColorTheme } from '@jeraisonne/db/cards';
import { MathText } from '@jeraisonne/ui';
import { OrigamiCreature, cardLevelRange } from '@jeraisonne/ui/tool-cards';
import { AvatarAura } from './avatar-aura/avatar-aura';

export function SkillcardBuyHero({
  title,
  levels,
  colorTheme,
  creature,
  xpMax,
}: {
  title: string;
  levels: string[];
  colorTheme: CardColorTheme | null | undefined;
  creature: unknown;
  xpMax: number;
}) {
  const palette = cardPalette(levels, colorTheme);
  const range = cardLevelRange(levels);
  // Teinte de la carte en variables CSS (seules valeurs non statiques).
  const vars = { '--jr-c-accent': palette.accent, '--jr-c-bg': palette.bg } as CSSProperties;

  return (
    <div
      data-testid="skillcard-buy-hero"
      style={vars}
      className="relative overflow-hidden rounded-3xl bg-[radial-gradient(circle_at_50%_38%,color-mix(in_srgb,var(--jr-c-accent)_55%,var(--jr-c-bg))_0%,var(--jr-c-bg)_68%)] px-4 pb-6 pt-4 text-center shadow-[0_18px_50px_-20px_rgba(0,0,0,0.6)] ring-1 ring-[color-mix(in_srgb,var(--jr-c-accent)_45%,transparent)]"
    >
      <p className="font-display text-sm font-extrabold uppercase tracking-[0.22em] text-[var(--jr-c-accent)] sm:text-base">
        Débloque ta SKILLCARD
      </p>

      <AvatarAura
        power={100}
        color={palette.accent}
        className="mx-auto mt-2 h-64 w-64 sm:h-72 sm:w-72"
      >
        <div className="flex h-32 w-32 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--jr-c-bg)_80%,black)] sm:h-36 sm:w-36">
          <OrigamiCreature
            creature={creature}
            accent={palette.accent}
            evolved
            className="h-[88%] w-[88%] drop-shadow-[0_6px_14px_rgba(0,0,0,0.5)]"
          />
        </div>
      </AvatarAura>

      <p className="font-display mx-auto -mt-2 inline-flex items-center gap-1.5 rounded-full border border-[var(--jr-c-accent)] bg-[color-mix(in_srgb,var(--jr-c-accent)_25%,transparent)] px-3 py-1 text-xs font-extrabold uppercase tracking-wider text-white">
        ✦ HORA MAX · {formatHora(xpMax)}
      </p>
      <h1 className="font-display mt-3 text-xl font-extrabold leading-tight text-white drop-shadow sm:text-2xl">
        <MathText>{title}</MathText>
      </h1>
      {range && <p className="mt-1 text-sm font-semibold text-white/75">{range}</p>}
      <p className="mx-auto mt-2 max-w-sm text-sm text-white/80">
        Débloque-la, gagne de l’HORA et maîtrise la compétence.
      </p>
    </div>
  );
}
